import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { quizzes } from "./quizzes";

export const Quiz = () => {
  const { difficulty } = useParams();

  const questions = quizzes[difficulty];

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // multiple choice
  const [selected, setSelected] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);

  // input (multi-answer)
  const [input, setInput] = useState("");
  const [guessed, setGuessed] = useState([]);
  const [showAnswers, setShowAnswers] = useState(false);

  // ✅ NEW: single input
  const [singleInput, setSingleInput] = useState("");
  const [singleCorrect, setSingleCorrect] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];

  // MULTIPLE CHOICE HANDLER
  const handleSelect = (option) => {
    setSelected(option);
    setIsCorrect(option === currentQuestion.correctAnswer);
  };

  // INPUT HANDLER (multi-answer)
  const handleChange = (e) => {
    const value = e.target.value.replace(/\s/g, "").toUpperCase();
    setInput(value);

    const normalized = value.trim();

    if (
      currentQuestion.answers.includes(normalized) &&
      !guessed.includes(normalized)
    ) {
      setGuessed((prev) => [...prev, normalized]);
      setInput("");
    }
  };

  // ✅ NEW: SINGLE INPUT HANDLER
  const handleSingleInputChange = (e) => {
    const value = e.target.value;
    setSingleInput(value);

    const normalized = value.trim().toLowerCase();
    const correct = currentQuestion.answer.trim().toLowerCase();

    if (normalized === correct) {
      setSingleCorrect(true);
    }
  };

  const handleNextQuestion = () => {
    setCurrentQuestionIndex((prev) => prev + 1);

    setSelected(null);
    setIsCorrect(false);

    setInput("");
    setGuessed([]);
    setShowAnswers(false);

    // reset single input
    setSingleInput("");
    setSingleCorrect(false);
  };

  const handleTryAgain = () => {
    setGuessed([]);
    setInput("");
    setShowAnswers(false);
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);

    setSelected(null);
    setIsCorrect(false);

    setInput("");
    setGuessed([]);
    setShowAnswers(false);

    setSingleInput("");
    setSingleCorrect(false);
  };

  // completion logic
  const isComplete =
    (currentQuestion.type === "multiple" && isCorrect) ||
    (currentQuestion.type === "input" &&
      guessed.length === currentQuestion.answers.length) ||
    (currentQuestion.type === "singleInput" && singleCorrect);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.key === "ArrowRight" &&
        currentQuestionIndex < questions.length - 1
      ) {
        e.preventDefault();
        handleNextQuestion();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentQuestionIndex, questions.length]);

  return (
    <div className="page">
      <h4>
        Question {currentQuestionIndex + 1} / {questions.length}
      </h4>

      <h2>{currentQuestion.question}</h2>

      {/* ✅ MULTIPLE CHOICE */}
      {currentQuestion.type === "multiple" && (
        <div style={{ marginTop: "20px" }}>
          {currentQuestion.options.map((option, idx) => {
            const isSelected = selected === option;
            const isRight = option === currentQuestion.correctAnswer;

            return (
              <button
                key={idx}
                onClick={() => handleSelect(option)}
                style={{
                  display: "block",
                  marginBottom: "10px",
                  padding: "10px",
                  width: "100%",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  background:
                    selected === null
                      ? "#e5e7eb"
                      : isSelected && isRight
                        ? "#10b981"
                        : isSelected && !isRight
                          ? "#ef4444"
                          : "#e5e7eb",
                }}
              >
                {option}
              </button>
            );
          })}
        </div>
      )}

      {/* ✅ INPUT QUESTIONS (multi-answer) */}
      {currentQuestion.type === "input" && (
        <>
          <h3>
            {guessed.length} / {currentQuestion.answers.length} correct
          </h3>

          {guessed.length === currentQuestion.answers.length && (
            <h3 style={{ color: "green", marginTop: "10px" }}>
              Perfect score ({currentQuestion.answers.length} /{" "}
              {currentQuestion.answers.length}) 🎉
            </h3>
          )}

          {!showAnswers &&
            guessed.length !== currentQuestion.answers.length && (
              <input
                type="text"
                value={input}
                onChange={handleChange}
                placeholder="Enter a site..."
                maxLength={2}
                autoFocus
              />
            )}

          <div style={{ marginTop: "20px" }}>
            <h4>Correct Answers:</h4>
            <ul>
              {[...currentQuestion.answers]
                .sort((a, b) => {
                  const isNumA = !isNaN(a);
                  const isNumB = !isNaN(b);

                  if (isNumA && isNumB) return Number(a) - Number(b);
                  if (isNumA) return -1;
                  if (isNumB) return 1;

                  return a.localeCompare(b);
                })
                .map((ans) => {
                  const isGuessed = guessed.includes(ans);
                  const isMissing = showAnswers && !isGuessed;

                  return (
                    <li
                      key={ans}
                      style={{
                        color: isGuessed
                          ? "black"
                          : isMissing
                            ? "green"
                            : "transparent",
                      }}
                    >
                      {isGuessed || showAnswers ? ans : ""}
                    </li>
                  );
                })}
            </ul>
          </div>

          {!showAnswers &&
            guessed.length !== currentQuestion.answers.length && (
              <button
                onClick={() => setShowAnswers(true)}
                style={{
                  marginTop: "15px",
                  padding: "10px 18px",
                  borderRadius: "8px",
                  border: "none",
                  background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                  color: "white",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Show Answers
              </button>
            )}
        </>
      )}

      {/* ✅ NEW: SINGLE INPUT QUESTION */}
      {currentQuestion.type === "singleInput" && (
        <div style={{ marginTop: "20px" }}>
          {!singleCorrect && (
            <input
              type="text"
              value={singleInput}
              onChange={handleSingleInputChange}
              placeholder="Type your answer..."
              autoFocus
              style={{
                padding: "10px",
                width: "100%",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
              }}
            />
          )}

          {singleCorrect && (
            <h3 style={{ color: "#10b981", marginTop: "10px" }}>Correct! ✅</h3>
          )}
        </div>
      )}

      {/* ✅ BUTTONS */}
      {(currentQuestion.type === "multiple" && isCorrect) ||
      (currentQuestion.type === "input" &&
        (showAnswers || guessed.length === currentQuestion.answers.length)) ||
      (currentQuestion.type === "singleInput" && singleCorrect) ? (
        <div style={{ marginTop: "15px", display: "flex", gap: "10px" }}>
          {/* Try Again (INPUT ONLY) */}
          {currentQuestion.type === "input" &&
            guessed.length !== currentQuestion.answers.length && (
              <button
                onClick={handleTryAgain}
                style={{
                  padding: "10px 16px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#f59e0b",
                  color: "white",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Try Again
              </button>
            )}

          {/* Next */}
          {currentQuestionIndex < questions.length - 1 && (
            <button
              onClick={handleNextQuestion}
              style={{
                padding: "10px 16px",
                borderRadius: "8px",
                border: "none",
                background: "#10b981",
                color: "white",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Next Question
            </button>
          )}

          {/* Restart */}
          {currentQuestionIndex === questions.length - 1 && isComplete && (
            <button
              onClick={handleRestart}
              style={{
                padding: "10px 16px",
                borderRadius: "8px",
                border: "none",
                background: "#3b82f6",
                color: "white",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Back to Start
            </button>
          )}
        </div>
      ) : null}

      {/* KEYBOARD SHORTCUTS */}
      <div
        style={{
          marginTop: "160px",
          paddingTop: "20px",
          borderTop: "1px solid #e5e7eb",
          textAlign: "center",
          color: "#6b7280",
          fontSize: "14px",
        }}
      >
        <h4 style={{ marginBottom: "10px", color: "#374151" }}>
          Keyboard Shortcuts
        </h4>

        <div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "32px",
              height: "32px",
              padding: "0 10px",
              borderRadius: "6px",
              background: "#f3f4f6",
              border: "1px solid #d1d5db",
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              marginRight: "8px",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            Right Arrow
          </span>
          Next Question
        </div>
      </div>
    </div>
  );
};
